"use client";

import { RefObject, useEffect, useRef } from "react";

/**
 * Plays a `<video>` element when it scrolls into the viewport and pauses it
 * when it leaves. Avoids autoplaying video everywhere on the page (a major
 * Lighthouse performance hit) while preserving the look-and-feel of "always
 * playing" hero/marketing videos.
 *
 * Convention used across the marketing surfaces:
 *
 *   - With a `poster` JPEG → `preload="none"` (the poster already fills the
 *     rectangle, saves bandwidth on the LCP-critical hero).
 *   - Without a `poster`    → `preload="metadata"` so the browser lazily
 *     pulls just enough video bytes (~hundreds of KB, post-interactive) to
 *     render the first frame natively, instead of leaving an empty grey
 *     box until the user scrolls the section into view.
 *
 * Never use `preload="none"` without a poster — it leaves a dead-looking
 * grey rectangle that breaks the marketing aesthetic.
 *
 * Usage:
 *   const ref = useInViewVideo<HTMLVideoElement>();
 *   // With poster:
 *   <video ref={ref} src="..." poster="..." muted loop playsInline preload="none" />
 *   // Without poster:
 *   <video ref={ref} src="..." muted loop playsInline preload="metadata" />
 */
export function useInViewVideo<T extends HTMLVideoElement>(options?: {
  threshold?: number;
  rootMargin?: string;
}): RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.play().catch(() => {});
      return;
    }

    // Track the last-known intersection state so we can resume playback
    // when the tab becomes visible again. Without this, switching tabs
    // pauses the video and `IntersectionObserver` never re-fires (the
    // element hasn't moved), so the video would stay stuck paused.
    let isIntersecting = false;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target as HTMLVideoElement;
          isIntersecting = entry.isIntersecting;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      {
        threshold: options?.threshold ?? 0.25,
        rootMargin: options?.rootMargin ?? "0px",
      }
    );

    observer.observe(el);

    const onVisibility = () => {
      if (document.hidden) {
        el.pause();
      } else if (isIntersecting) {
        el.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [options?.threshold, options?.rootMargin]);

  return ref;
}

export default useInViewVideo;
